import { useState, useMemo } from 'react';
import { Search, LayoutGrid, List, Building2, Clock, SlidersHorizontal, CheckCircle2, UserCheck, CalendarDays } from 'lucide-react';
import { useAuditorStore, switchAuditorCompany } from '../../stores/auditorStore';
import { useAuthStore } from '../../stores/authStore';
import { AuditorCompanyCard } from '../../components/auditor/AuditorCompanyCard';
import { AuditorCompanyRow } from '../../components/auditor/AuditorCompanyRow';
import { CreditScoreCETModal } from '../../components/auditor/CreditScoreCETModal';
import { useCreditScores } from '../../hooks/useCreditScores';
import type { AuditorCompany } from '../../types/auditor';
import { useNavigate } from 'react-router-dom';

type ViewMode = 'grid' | 'table';
type SortMode = 'recent' | 'name' | 'date-added';
type StatusFilter = 'all' | 'active' | 'inactive';

export function AuditorClientsPage() {
  const navigate = useNavigate();
  const { auditorProfile, recentlyOpenedIds } = useAuditorStore();
  const { user } = useAuthStore();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  // ─── CET modal state ──────────────────────────────────────────────────
  const [cetCompany, setCetCompany] = useState<AuditorCompany | null>(null);

  const companies: AuditorCompany[] = auditorProfile?.companies ?? [];

  // ─── Credit scores (via reusable hook) ─────────────────────────────────
  const companyIds = useMemo(() => companies.map((c: AuditorCompany) => c.organization.id), [companies]);
  const { scoreMap, refreshScore } = useCreditScores(companyIds);

  const hasNoClients = companies.length === 0;

  // Search + sort + status
  const filtered = useMemo(() => {
    let result = [...companies];

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.membership.status === statusFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((c) => {
        const org = c.organization;
        return (
          org.name.toLowerCase().includes(q) ||
          org.registrationNumber?.toLowerCase().includes(q) ||
          org.taxId?.toLowerCase().includes(q) ||
          org.city?.toLowerCase().includes(q) ||
          org.legalForm?.toLowerCase().includes(q)
        );
      });
    }

    // Sort
    switch (sortMode) {
      case 'recent': {
        result.sort((a, b) => {
          const aIdx = recentlyOpenedIds.indexOf(a.organization.id);
          const bIdx = recentlyOpenedIds.indexOf(b.organization.id);
          if (aIdx === -1 && bIdx === -1) return 0;
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
        break;
      }
      case 'name':
        result.sort((a, b) => a.organization.name.localeCompare(b.organization.name));
        break;
      case 'date-added':
        result.sort(
          (a, b) =>
            new Date(b.membership.addedAt).getTime() -
            new Date(a.membership.addedAt).getTime()
        );
        break;
    }

    return result;
  }, [companies, search, sortMode, recentlyOpenedIds]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page on search change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleOpenCompany = async (company: AuditorCompany) => {
    await switchAuditorCompany(company);
    navigate('/');
  };

  const isRecentlyOpened = (orgId: string) => recentlyOpenedIds.includes(orgId);

  const handleCetClick = (company: AuditorCompany) => {
    setCetCompany(company);
  };

  // Recently opened companies (top section)
  const recentCompanies = useMemo(() => {
    if (recentlyOpenedIds.length === 0) return [];
    return recentlyOpenedIds
      .slice(0, 4)
      .map((id) => companies.find((c) => c.organization.id === id))
      .filter(Boolean) as AuditorCompany[];
  }, [recentlyOpenedIds, companies]);

  // KPI stats
  const kpiStats = useMemo(() => {
    const total = companies.length;
    const active = companies.filter(c => c.membership.status === 'active').length;
    const thisMonth = companies.filter(c => {
      const added = new Date(c.membership.addedAt);
      const now = new Date();
      return added.getMonth() === now.getMonth() && added.getFullYear() === now.getFullYear();
    }).length;
    return { total, active, thisMonth };
  }, [companies]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Mes Clients
        </h1>
        <p className="text-muted-foreground mt-1">
          {auditorProfile?.totalCompanies ?? 0} entreprise{(auditorProfile?.totalCompanies ?? 0) > 1 ? 's' : ''} sous audit
        </p>
      </div>

      {/* KPI Stats */}
      {!hasNoClients && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl px-5 py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{kpiStats.total}</p>
              <p className="text-xs text-muted-foreground">Total clients</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl px-5 py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{kpiStats.active}</p>
              <p className="text-xs text-muted-foreground">Mandats actifs</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl px-5 py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{kpiStats.thisMonth}</p>
              <p className="text-xs text-muted-foreground">Ajoutés ce mois</p>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding: aucun client associé */}
      {hasNoClients && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <Building2 className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Aucune PME associée pour le moment
          </h2>
          <p className="text-muted-foreground max-w-md mb-2">
            Les PME que vous accompagnez apparaîtront ici dès qu'elles vous ajouteront
            en tant qu'auditeur ou expert-comptable dans leur espace Wanzo.
          </p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Communiquez votre adresse e-mail (<span className="font-medium text-foreground">{user?.email}</span>)
            à vos clients pour qu'ils puissent vous inviter depuis leur module Utilisateurs.
          </p>
        </div>
      )}

      {/* Toolbar: Search + Status Filter + View toggle + Sort */}
      {!hasNoClients && (
      <div className="flex flex-col gap-3">
        {/* Row 1: Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher par nom, RCCM, NIF, ville..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Sort */}
            <div className="relative flex-1 sm:flex-initial">
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="w-full appearance-none pl-8 pr-8 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
              >
                <option value="recent">Récemment ouvert</option>
                <option value="name">Nom (A-Z)</option>
                <option value="date-added">Date d'ajout</option>
              </select>
              <SlidersHorizontal className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            </div>

            {/* View mode */}
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="Affichage grille"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 transition-colors ${
                  viewMode === 'table'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="Affichage tableau"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Status filter chips */}
        <div className="flex items-center gap-2">
          {([['all', 'Tous'], ['active', 'Actifs'], ['inactive', 'Inactifs']] as [StatusFilter, string][]).map(([value, label]) => (
            <button
              key={value}
              onClick={() => { setStatusFilter(value); setCurrentPage(1); }}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                statusFilter === value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
              }`}
            >
              {label}
              {value === 'all' && ` (${companies.length})`}
              {value === 'active' && ` (${kpiStats.active})`}
              {value === 'inactive' && ` (${companies.length - kpiStats.active})`}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Recently Opened Section */}
      {!hasNoClients && recentCompanies.length > 0 && !search && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Ouverts récemment
            </h2>
          </div>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentCompanies.map((company) => (
                <AuditorCompanyCard
                  key={company.organization.id}
                  company={company}
                  isRecentlyOpened
                  onOpen={handleOpenCompany}
                  creditScore={scoreMap[company.organization.id]}
                  onCetClick={handleCetClick}
                />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Entreprise</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">RCCM</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">NIF</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Devise</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Coordonnées</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Exercices</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Membre</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cote / CET</th>
                      <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCompanies.map((company) => (
                      <AuditorCompanyRow
                        key={company.organization.id}
                        company={company}
                        isRecentlyOpened
                        onOpen={handleOpenCompany}
                        creditScore={scoreMap[company.organization.id]}
                        onCetClick={handleCetClick}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Clients heading */}
      {!hasNoClients && (
      <>
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {search ? `Résultats (${filtered.length})` : 'Tous les clients'}
        </h2>
      </div>

      {/* Content */}
      {paginated.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">
            {search ? 'Aucun résultat' : 'Aucun client'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {search
              ? "Essayez avec d'autres termes de recherche."
              : "Aucune entreprise ne vous a encore ajouté comme auditeur ou consultant."}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginated.map((company) => (
            <AuditorCompanyCard
              key={company.organization.id}
              company={company}
              isRecentlyOpened={isRecentlyOpened(company.organization.id)}
              onOpen={handleOpenCompany}
              creditScore={scoreMap[company.organization.id]}
              onCetClick={handleCetClick}
            />
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b border-border text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Entreprise
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                    RCCM
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                    NIF
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Devise
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Coordonnées
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">
                    Exercices
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">
                    Membre
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Cote / CET
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((company) => (
                  <AuditorCompanyRow
                    key={company.organization.id}
                    company={company}
                    isRecentlyOpened={isRecentlyOpened(company.organization.id)}
                    onOpen={handleOpenCompany}
                    creditScore={scoreMap[company.organization.id]}
                    onCetClick={handleCetClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Page {currentPage} sur {totalPages} — {filtered.length} entreprise{filtered.length > 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <span className="hidden sm:inline">Précédent</span>
              <span className="sm:hidden">&larr;</span>
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Show pages around current
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                    page === currentPage
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border bg-background text-foreground hover:bg-muted'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <span className="hidden sm:inline">Suivant</span>
              <span className="sm:hidden">&rarr;</span>
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {/* CET Modal */}
      <CreditScoreCETModal
        isOpen={!!cetCompany}
        onClose={() => setCetCompany(null)}
        companyId={cetCompany?.organization.id ?? ''}
        companyName={cetCompany?.organization.name ?? ''}
        onScoreUpdated={refreshScore}
      />
    </div>
  );
}
